import type { ActionContext } from 'vuex';
import { getCrmRequests } from '../api/api';
import type { GetOpportunitiesParams } from '../interfaces/api';
import type { Opportunity, PipelineStage } from '../interfaces/opportunity';
import type { CrmState, OpportunityFilterPayload, OpportunityState } from './types';

let latestFetchRequestId = 0;

const defaultState = (): OpportunityState => ({
    opportunities: [],
    pagination: {
        totalElementCount: 0,
        totalPageCount: 0,
        elementsPerPage: 25,
        currentPage: 1,
    },
    loading: false,
    error: null,
    filters: {
        search: '',
        order: '',
        startDate: null,
        endDate: null,
    },
});

const mutations = {
    SET_OPPORTUNITIES(state: OpportunityState, opportunities: Opportunity[]): void {
        state.opportunities = opportunities;
    },
    SET_PAGINATION(state: OpportunityState, pagination: OpportunityState['pagination']): void {
        state.pagination = pagination;
    },
    SET_LOADING(state: OpportunityState, loading: boolean): void {
        state.loading = loading;
    },
    SET_ERROR(state: OpportunityState, error: string | null): void {
        state.error = error;
    },
    SET_FILTER(state: OpportunityState, filter: { key: keyof OpportunityState['filters']; value: unknown }): void {
        (state.filters as unknown as Record<string, unknown>)[filter.key] = filter.value;
        state.pagination.currentPage = 1;
    },
    SET_PAGE(state: OpportunityState, page: number): void {
        state.pagination.currentPage = page;
    },
    SET_ITEMS_PER_PAGE(state: OpportunityState, itemsPerPage: number): void {
        state.pagination.elementsPerPage = itemsPerPage;
        state.pagination.currentPage = 1;
    },
    APPLY_FILTERS(state: OpportunityState, payload: OpportunityFilterPayload): void {
        if (payload.reset) {
            state.filters.search = '';
            state.filters.order = '';
        }
        if ('startDate' in payload) state.filters.startDate = payload.startDate ?? null;
        if ('endDate' in payload) state.filters.endDate = payload.endDate ?? null;
        state.pagination.currentPage = 1;
    },
};

const actions = {
    async fetchOpportunities(
        { commit, getters, state }: ActionContext<CrmState, unknown>,
        params: Partial<GetOpportunitiesParams> = {},
    ): Promise<void> {
        const configuration = params.configuration ?? (getters.selectedPipelineId as string | null);
        if (!configuration) {
            return;
        }

        const requestId = ++latestFetchRequestId;
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        try {
            const { search, order, startDate, endDate } = state.filters;
            const mergedParams: GetOpportunitiesParams = {
                configuration,
                search: search || undefined,
                order: order || undefined,
                startDate: startDate ?? undefined,
                endDate: endDate ?? undefined,
                pagination: false,
                ...params,
            };
            const data = await getCrmRequests().getOpportunities(mergedParams);
            if (requestId !== latestFetchRequestId) return;
            commit('SET_OPPORTUNITIES', data.opportunities);
            commit('SET_PAGINATION', {
                ...data.pagination,
                elementsPerPage: state.pagination.elementsPerPage,
                currentPage: 1,
            });
            commit('SET_STAGES', data.stages as PipelineStage[]);
        } catch {
            if (requestId !== latestFetchRequestId) return;
            commit('SET_ERROR', 'crm.board.error.description');
        } finally {
            if (requestId === latestFetchRequestId) {
                commit('SET_LOADING', false);
            }
        }
    },
    async setSearch(
        { commit, dispatch }: ActionContext<CrmState, unknown>,
        search: string,
    ): Promise<void> {
        commit('SET_FILTER', { key: 'search', value: search });
        await dispatch('fetchOpportunities');
    },
    async setOrder(
        { commit, dispatch }: ActionContext<CrmState, unknown>,
        order: string,
    ): Promise<void> {
        commit('SET_FILTER', { key: 'order', value: order });
        await dispatch('fetchOpportunities');
    },
    async applyOpportunityFilters(
        { commit, dispatch }: ActionContext<CrmState, unknown>,
        value: Record<string, unknown>,
    ): Promise<void> {
        const payload: OpportunityFilterPayload = {
            ...(typeof value.startDate === 'string' || value.startDate === null
                ? { startDate: value.startDate }
                : {}),
            ...(typeof value.endDate === 'string' || value.endDate === null
                ? { endDate: value.endDate }
                : {}),
            reset: value.reset === true,
        };
        commit('APPLY_FILTERS', payload);
        await dispatch('fetchOpportunities');
    },
};

const getters = {
    opportunities: (state: OpportunityState): Opportunity[] => state.opportunities,
    allOpportunities: (state: OpportunityState): Opportunity[] => state.opportunities,
    boardStages: (state: CrmState): PipelineStage[] => [...state.stages].sort((a, b) => a.order - b.order),
    opportunitiesPagination: (state: OpportunityState): OpportunityState['pagination'] => {
        const totalElementCount = state.opportunities.length;
        return {
            ...state.pagination,
            totalElementCount,
            totalPageCount: Math.ceil(totalElementCount / state.pagination.elementsPerPage),
        };
    },
    paginatedOpportunities: (state: OpportunityState): Opportunity[] => {
        const start = (state.pagination.currentPage - 1) * state.pagination.elementsPerPage;
        return state.opportunities.slice(start, start + state.pagination.elementsPerPage);
    },
    opportunitiesLoading: (state: OpportunityState): boolean => state.loading,
    opportunitiesError: (state: OpportunityState): string | null => state.error,
    opportunityFilters: (state: OpportunityState): OpportunityState['filters'] => state.filters,
};

/** Exported as plain fragments (not a standalone `Module`) so `store/index.ts` can merge them with
 * `pipeline.ts` into a single namespaced `crm` module — see FRONTEND_PATTERNS.md's store module
 * pattern and the Phase 1 spec ("composed into `crmStoreModules` as a single namespaced `crm`
 * module"). */
export const opportunityModule = {
    state: defaultState,
    mutations,
    actions,
    getters,
};
